import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LifeBuoy, X, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Client-side crisis pre-screen banner.
 *
 * Surfaces a soft, non-alarming inline panel under the letter textarea WHILE
 * the user is typing — paired with the Claude server-side safety override
 * (which only fires at delivery, 90–365 days later), this gives users a way
 * to reach a real human RIGHT NOW.
 *
 * Design notes:
 *   • Not red, not loud. Soft wax-tinted border, mute tone — meets, does not startle.
 *   • Dismissible once per session so the moment the user has been seen, it stops nagging.
 *   • Primary CTA expands an inline list of regional helplines instead of yanking the
 *     user out of the page.
 */
export const CrisisBanner = ({ visible, onDismiss }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const isZH = (i18n.language || "en").startsWith("zh");
  if (!visible) return null;
  const helplines = t("crisis_banner.helplines", { returnObjects: true }) || [];
  return (
    <div
      data-testid="crisis-banner"
      role="status"
      className="mt-3 border border-[var(--wax)]/35 bg-[var(--wax)]/[0.06] px-5 py-4"
    >
      <div className="flex items-start gap-3.5">
        <LifeBuoy size={18} className="text-[var(--wax)] shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className={`${isZH ? "font-cn" : "font-display"} text-[15px] md:text-[16px] text-[var(--ink)] leading-snug`}>
            {t("crisis_banner.headline")}
          </p>
          <p className="text-[13px] italic text-[var(--ink-soft)] mt-1.5 leading-[1.7]">
            {t("crisis_banner.subtext")}
          </p>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="crisis-banner-cta"
            aria-expanded={open}
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.22em] text-[var(--wax)] hover:text-[var(--ink)] border-b border-[var(--wax)]/60 hover:border-[var(--ink)] pb-0.5"
          >
            {t("crisis_banner.cta")}
            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {open && Array.isArray(helplines) && helplines.length > 0 && (
            <ul
              data-testid="crisis-banner-helplines"
              className="mt-4 space-y-2 text-[13px] leading-[1.7] text-[var(--ink)] border-t border-dashed border-[var(--wax)]/30 pt-4"
            >
              {helplines.map((h, i) => (
                <li key={i} className="flex flex-col sm:flex-row sm:gap-3">
                  <span className="font-medium text-[var(--ink)] sm:min-w-[120px]">{h.region}</span>
                  <span className="text-[var(--ink-soft)] italic">{h.line}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="crisis-banner-dismiss"
          aria-label={t("crisis_banner.dismiss")}
          title={t("crisis_banner.dismiss")}
          className="text-[var(--ink-mute)] hover:text-[var(--ink)] -mt-1 -mr-1 p-1"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
};

// -- Crisis-marker regex (combined). Matched against trimmed, lowercased text. --
// English uses whole-word boundaries; Chinese ideographs match directly.
// Two intent groups:
//   (A) Self-Harm / Suicidal ideation
//   (B) Harm-to-Others / Violence / Homicide
const CRISIS_REGEX = new RegExp(
  [
    // ---------- (A) SELF-HARM — English ----------
    "\\b(kill(ing)?\\s+myself|end(ing)?\\s+my\\s+life|want(ing)?\\s+to\\s+die|going\\s+to\\s+(kill|end)|take\\s+my\\s+(own\\s+)?life|no\\s+reason\\s+to\\s+live|don'?t\\s+want\\s+to\\s+(live|wake|exist)|hurt(ing)?\\s+myself|cut(ting)?\\s+myself|self[-\\s]?harm(ing)?|overdose|suicid(e|al)|jump\\s+off|hang(ing)?\\s+myself|i'?m\\s+going\\s+to\\s+die|i\\s+want\\s+to\\s+die)\\b",
    // ---------- (A) SELF-HARM — Chinese (Traditional + Simplified) ----------
    "(想死|不想活|不想再活|活不下去|沒有活下去|没有活下去|自殺|自杀|自殘|自残|結束生命|结束生命|結束自己|结束自己|了結自己|了结自己|傷害自己|伤害自己|割傷自己|割伤自己|跳樓|跳楼|上吊|服藥自殺|服药自杀|我想死|我想自殺|我想自杀)",
    // ---------- (B) HARM-TO-OTHERS — English ----------
    "\\b(murder(ing|ed|er)?|homicide|kill\\s+(someone|him|her|them|you|people|everyone|all\\s+of\\s+(them|you))|killing\\s+(someone|him|her|them|people|everyone)|hurt(ing)?\\s+(others|someone|people|him|her|them)|shoot(ing)?\\s+(up|someone|him|her|them|the\\s+place)|stab(bing|bed)?\\s+(someone|him|her|them)|beat(ing)?\\s+(him|her|them)\\s+to\\s+death|attack(ing)?\\s+(him|her|them|someone))\\b",
    // ---------- (B) HARM-TO-OTHERS — Chinese (Traditional + Simplified) ----------
    "(謀殺|谋杀|殺害|杀害|殺掉|杀掉|害死|殺人|杀人|殺所有人|杀所有人|殺光|杀光|殺了他|杀了他|殺了她|杀了她|殺了你|杀了你|殺了你們|杀了你们|傷害別人|伤害别人|傷害他人|伤害他人|傷害你|伤害你|打死他|打死她|打死你|滅口|灭口|我要謀殺|我要谋杀|我要殺|我要杀)",
  ].join("|"),
  "i"
);

export const detectCrisis = (text) => {
  if (!text) return false;
  // Trim whitespace and normalize — CJK strings can be very short ("我想死" = 3 chars)
  // so the length gate must be tiny to catch dense ideographic phrases.
  const normalized = String(text).trim();
  if (normalized.length < 2) return false;
  return CRISIS_REGEX.test(normalized);
};

export default CrisisBanner;
